import { useState, useEffect, useCallback, useMemo } from 'react';

import { fetchAutocomplete } from './api';

import type { Suggestion } from '@/components/chat-search/types';

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function useAutocomplete({
  query,
  endpoint,
  allowedRegions,
  collectionId,
  headers,
  conversationId,
  delay = 300,
}: {
  query: string;
  endpoint: string;
  allowedRegions?: string[];
  collectionId?: string;
  headers?: Record<string, string>;
  conversationId?: string;
  delay?: number;
}) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();

  const debouncedQuery = useDebounce(query, delay);

  const fetchSuggestions = useCallback(async () => {
    if (!debouncedQuery.trim()) {
      setSuggestions([]);

      return;
    }

    setIsLoading(true);
    setError(undefined);

    try {
      const payload = await fetchAutocomplete({
        endpoint,
        query: debouncedQuery,
        collectionId,
        headers,
        conversationId,
        allowedRegions,
      });
      if (payload?.response?.results) {
        // move suggestions that don't prefix with the debounced question to the bottom
        const lowerCaseQuery = debouncedQuery.toLowerCase();
        const sortedSuggestions = payload.response.results.sort((a: { question: string }, b: { question: string }) => {
          const aStartsWithQuery = a.question.toLowerCase().startsWith(lowerCaseQuery);
          const bStartsWithQuery = b.question.toLowerCase().startsWith(lowerCaseQuery);

          if (aStartsWithQuery && !bStartsWithQuery) return -1;
          if (!aStartsWithQuery && bStartsWithQuery) return 1;

          return 0; // preserve original order for items in same category
        });

        setSuggestions(sortedSuggestions);
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      console.error('Error fetching autocomplete suggestions:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch suggestions');
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedQuery, endpoint, collectionId, headers, conversationId, allowedRegions]);

  useEffect(() => {
    void fetchSuggestions();
  }, [fetchSuggestions]);

  return { suggestions, isLoading, error };
}
/** Stable `{ contains }` for case-insensitive substring checks when filtering lists. */
export function useSubstringFilter() {
  return useMemo(
    () => ({
      contains: (text: string, substring: string) => text.toLowerCase().includes(substring.toLowerCase()),
    }),
    [],
  );
}
